<script setup lang="ts">
import { ref, watch } from 'vue'
import Box from '@uiKit/elements/Box.vue'
import AppButton from '@uiKit/elements/Button.vue'
import Text from '@uiKit/elements/Text.vue'
import PiIcon from '@uiKit/elements/PiIcon.vue'
import { maskAccountNumber } from '@shared/utils'
import type { BankAccount } from '@modules/account'

interface Props {
  visible: boolean
  accounts: BankAccount[]
  selectedId: string
}

const props = defineProps<Props>()

const emit = defineEmits<{
  (e: 'update:visible', val: boolean): void
  (e: 'select', account: BankAccount): void
}>()

const pendingId = ref(props.selectedId)

watch(
  () => props.selectedId,
  (val) => (pendingId.value = val),
)

watch(
  () => props.visible,
  (val) => {
    if (val) pendingId.value = props.selectedId
  },
)

const isVerified = (account: BankAccount) => account.accountStatus === 'verified'

const statusLabel = (status: string) => {
  if (status === 'verified') return ''
  if (status === 'rejected') return 'Rejected'
  return 'Pending'
}

const onDone = () => {
  const account = props.accounts.find((candidate) => candidate.id === pendingId.value)
  if (account) emit('select', account)
  emit('update:visible', false)
}

const onCancel = () => emit('update:visible', false)
</script>

<template>
  <Teleport to="body">
    <Transition name="sbm-fade">
      <Box v-if="visible" align="center" justify="center" class="sbm__backdrop" @click.self="onCancel">
        <Transition name="sbm-slide">
          <Box v-if="visible" direction="col" class="sbm__panel">
            <Box align="center" justify="between" class="sbm__header">
              <Text type="page-title" is="span">Select Bank Account</Text>
              <Box
                is="button"
                flex
                align="center"
                justify="center"
                class="sbm__close"
                type="button"
                aria-label="Close"
                @click="onCancel"
              >
                <PiIcon name="times" style="font-size: 16px; color: var(--p-text-muted-color)" />
              </Box>
            </Box>

            <Box direction="col" gap="2" class="sbm__list">
              <Box
                v-for="account in accounts"
                :key="account.id"
                is="label"
                align="center"
                gap="3"
                class="sbm__item"
                :class="{
                  'sbm__item--selected': pendingId === account.id,
                  'sbm__item--disabled': !isVerified(account),
                }"
              >
                <!-- Native radio input — accent-color is the kit-compliant style. §19 exception. -->
                <input
                  type="radio"
                  class="sbm__radio"
                  :value="account.id"
                  :disabled="!isVerified(account)"
                  :checked="pendingId === account.id"
                  @change="pendingId = account.id"
                />

                <Box flex align="center" justify="center" class="sbm__bank-icon">
                  <PiIcon name="building-columns" style="font-size: 16px; color: var(--p-info-600)" />
                </Box>

                <Box direction="col" gap="1" grow class="sbm__bank-info">
                  <Text type="body" weight="medium" is="span">{{ account.bankName }}</Text>
                  <Text type="caption" color="muted" is="span">
                    {{ maskAccountNumber(account.accountNumber) }}
                  </Text>
                </Box>

                <Box gap="2" class="sbm__badges">
                  <Text
                    v-if="account.isPrimary"
                    type="caption-sm"
                    is="span"
                    class="sbm__badge sbm__badge--default"
                  >
                    Default
                  </Text>
                  <Text
                    v-if="!isVerified(account)"
                    type="caption-sm"
                    is="span"
                    class="sbm__badge"
                    :class="
                      account.accountStatus === 'rejected'
                        ? 'sbm__badge--rejected'
                        : 'sbm__badge--pending'
                    "
                  >
                    {{ statusLabel(account.accountStatus) }}
                  </Text>
                </Box>
              </Box>

              <Text v-if="accounts.length === 0" type="body" align="center" class="sbm__empty">
                No bank accounts found.
              </Text>
            </Box>

            <Box gap="3" class="sbm__actions">
              <AppButton label="Cancel" color="secondary" class="sbm__btn" @click="onCancel" />
              <AppButton label="Done" class="sbm__btn" @click="onDone" />
            </Box>
          </Box>
        </Transition>
      </Box>
    </Transition>
  </Teleport>
</template>

<style scoped>
.sbm__backdrop {
  position: fixed;
  inset: 0;
  background: rgba(10, 13, 18, 0.45);
  z-index: 1000;
  padding: 1rem;
}

.sbm__panel {
  background: var(--p-surface-0);
  border-radius: 16px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 20px 40px rgba(10, 13, 18, 0.18);
  overflow: hidden;
}

.sbm__header {
  padding: 1.25rem 1.5rem 1rem;
  border-bottom: 1px solid var(--p-surface-200);
  flex-shrink: 0;
}

.sbm__close {
  width: 32px;
  height: 32px;
  background: var(--p-surface-100);
  border: none;
  border-radius: 8px;
  cursor: pointer;
  transition: background 0.15s;
  flex-shrink: 0;
}

.sbm__close:hover {
  background: var(--p-surface-200);
}

.sbm__close:focus-visible {
  outline: 2px solid var(--p-primary-600);
  outline-offset: 2px;
}

.sbm__list {
  padding: 1rem 1.5rem;
  overflow-y: auto;
  max-height: 360px;
  scrollbar-width: thin;
  scrollbar-color: var(--p-content-border-color) transparent;
}

.sbm__item {
  padding: 14px 1rem;
  border: 1px solid var(--p-content-border-color);
  border-radius: var(--p-border-radius-lg);
  cursor: pointer;
  transition:
    border-color 0.15s,
    background 0.15s;
}

.sbm__item:hover:not(.sbm__item--disabled) {
  border-color: var(--p-info-200);
  background: var(--p-info-50);
}

.sbm__item--selected {
  border-color: var(--p-primary-600);
  background: var(--p-primary-50);
}

.sbm__item--disabled {
  cursor: not-allowed;
  opacity: 0.55;
}

.sbm__radio {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  accent-color: var(--p-primary-600);
  cursor: pointer;
}

.sbm__item--disabled .sbm__radio {
  cursor: not-allowed;
}

.sbm__bank-icon {
  width: 40px;
  height: 40px;
  background: var(--p-info-50);
  border: 1px solid var(--p-info-200);
  border-radius: 10px;
  flex-shrink: 0;
}

.sbm__bank-info {
  min-width: 0;
}

.sbm__badges {
  flex-shrink: 0;
}

.sbm__badge {
  line-height: 16px;
  padding: 2px 8px;
  border-radius: 999px;
}

.sbm__badge--default {
  background: var(--p-success-50);
  color: var(--p-success-700);
  border: 1px solid var(--p-success-300);
}

.sbm__badge--pending {
  background: var(--p-warning-50);
  color: var(--p-warning-700);
  border: 1px solid var(--p-warning-200);
}

.sbm__badge--rejected {
  background: var(--p-danger-50);
  color: var(--p-danger-700);
  border: 1px solid var(--p-danger-200);
}

.sbm__empty {
  color: var(--p-text-muted-color);
  padding: 1.5rem 0;
  margin: 0;
}

.sbm__actions {
  padding: 1rem 1.5rem 1.25rem;
  border-top: 1px solid var(--p-surface-200);
  flex-shrink: 0;
}

.sbm__btn {
  flex: 1;
}

.sbm-fade-enter-active,
.sbm-fade-leave-active {
  transition: opacity 0.2s ease;
}

.sbm-fade-enter-from,
.sbm-fade-leave-to {
  opacity: 0;
}

.sbm-slide-enter-active,
.sbm-slide-leave-active {
  transition:
    opacity 0.2s ease,
    transform 0.2s cubic-bezier(0.22, 1, 0.36, 1);
}

.sbm-slide-enter-from,
.sbm-slide-leave-to {
  opacity: 0;
  transform: translateY(16px) scale(0.97);
}
</style>
